import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiCalendar,
    FiChevronDown,
    FiFileText,
    FiHash,
    FiMapPin,
    FiRefreshCw,
    FiShoppingBag,
    FiTruck,
    FiUserCheck,
} from "react-icons/fi";

import "./InvoiceHeader.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const EMPTY_INVOICE_HEADER = {
    documentNumber: "",
    documentDate: "",
    dueDate: "",

    referenceNumber: "",
    purchaseOrderNumber: "",

    salesperson: "",
    salespersonId: "",

    salesChannel: "STORE",
    sourceType: "MANUAL",

    branchId: "",
    firmId: "",
    warehouseId: "",

    placeOfSupply: "",
    placeOfSupplyStateCode: "",
    isInterState: false,

    paymentTermDays: 0,

    shippingMethod: "",
    vehicleNumber: "",
    transporterName: "",

    documentStatus: "DRAFT",
};

const SALES_CHANNELS = [
    {
        value: "STORE",
        label: "Store",
    },
    {
        value: "ONLINE",
        label: "Online",
    },
    {
        value: "PHONE",
        label: "Phone",
    },
    {
        value: "WHATSAPP",
        label: "WhatsApp",
    },
    {
        value: "MARKETPLACE",
        label: "Marketplace",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];

const SOURCE_TYPES = [
    {
        value: "MANUAL",
        label: "Manual",
    },
    {
        value: "QUOTATION",
        label: "Quotation",
    },
    {
        value: "SALE_ORDER",
        label: "Sale Order",
    },
    {
        value: "DELIVERY_CHALLAN",
        label: "Delivery Challan",
    },
    {
        value: "ONLINE_ORDER",
        label: "Online Order",
    },
    {
        value: "IMPORT",
        label: "Imported",
    },
];

const PAYMENT_TERM_OPTIONS = [
    {
        value: 0,
        label: "Due on receipt",
    },
    {
        value: 7,
        label: "7 days",
    },
    {
        value: 15,
        label: "15 days",
    },
    {
        value: 30,
        label: "30 days",
    },
    {
        value: 45,
        label: "45 days",
    },
    {
        value: 60,
        label: "60 days",
    },
    {
        value: 90,
        label: "90 days",
    },
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const normalizeText = (
    value
) => String(
    value ?? ""
).trim();

const toDateInputValue = (
    value
) => {
    if (!value) {
        return "";
    }

    const date =
        value instanceof Date
            ? value
            : new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }

    const year =
        date.getFullYear();

    const month =
        String(
            date.getMonth() +
            1
        ).padStart(
            2,
            "0"
        );

    const day =
        String(
            date.getDate()
        ).padStart(
            2,
            "0"
        );

    return `${year}-${month}-${day}`;
};

const addDays = (
    dateValue,
    days
) => {
    const date =
        dateValue
            ? new Date(
                `${dateValue}T00:00:00`
            )
            : new Date();

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }

    date.setDate(
        date.getDate() +
        Number(days || 0)
    );

    return toDateInputValue(
        date
    );
};

const normalizeHeaderValue = (
    value
) => {
    const source =
        value &&
        typeof value ===
            "object"
            ? value
            : {};

    return {
        ...EMPTY_INVOICE_HEADER,
        ...source,

        documentNumber:
            source.documentNumber ||
            source.invoiceNumber ||
            source.quotationNumber ||
            "",

        documentDate:
            toDateInputValue(
                source.documentDate ||
                source.invoiceDate ||
                new Date()
            ),

        dueDate:
            toDateInputValue(
                source.dueDate
            ),

        paymentTermDays:
            Number(
                source.paymentTermDays ||
                0
            ),

        isInterState:
            source.isInterState ===
            true,
    };
};

const validateInvoiceHeader = (
    header,
    {
        requireDocumentDate =
            true,
        requireDueDate =
            false,
    } = {}
) => {
    const errors = {};

    if (
        requireDocumentDate &&
        !header.documentDate
    ) {
        errors.documentDate =
            "Document date is required.";
    }

    if (
        requireDueDate &&
        !header.dueDate
    ) {
        errors.dueDate =
            "Due date is required.";
    }

    if (
        header.documentDate &&
        header.dueDate &&
        new Date(
            `${header.dueDate}T00:00:00`
        ) <
            new Date(
                `${header.documentDate}T00:00:00`
            )
    ) {
        errors.dueDate =
            "Due date cannot be before document date.";
    }

    if (
        normalizeText(
            header
                .placeOfSupplyStateCode
        ) &&
        !/^\d{2}$/.test(
            normalizeText(
                header
                    .placeOfSupplyStateCode
            )
        )
    ) {
        errors.placeOfSupplyStateCode =
            "State code must be 2 digits.";
    }

    return errors;
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const InvoiceHeader = ({
    value,
    onChange,
    onValidationChange,

    documentType =
        "SALE_INVOICE",

    disabled = false,

    requireDocumentDate =
        true,

    requireDueDate =
        false,

    branches = [],
    firms = [],
    warehouses = [],
    salespersons = [],

    showDueDate = true,
    showReference = true,
    showPurchaseOrder = true,
    showSalesperson = true,
    showSalesChannel = true,
    showSourceType = true,
    showPlaceOfSupply = true,
    showBranch = true,
    showFirm = true,
    showWarehouse = true,
    showShipping = true,

    autoCalculateDueDate =
        true,
}) => {
    const [locationOpen, setLocationOpen] = useState(true);
    const [supplyOpen, setSupplyOpen] = useState(true);
    const [deliveryOpen, setDeliveryOpen] = useState(true);

    const header =
        useMemo(
            () =>
                normalizeHeaderValue(
                    value
                ),
            [value]
        );

    const errors =
        useMemo(
            () =>
                validateInvoiceHeader(
                    header,
                    {
                        requireDocumentDate,
                        requireDueDate,
                    }
                ),
            [
                header,
                requireDocumentDate,
                requireDueDate,
            ]
        );

    useEffect(() => {
        if (
            typeof onValidationChange ===
            "function"
        ) {
            onValidationChange({
                valid:
                    Object.keys(
                        errors
                    ).length ===
                    0,

                errors,

                header,
            });
        }
    }, [
        errors,
        header,
        // onValidationChange,
    ]);

    const emitChange =
        useCallback(
            (
                patch
            ) => {
                onChange?.({
                    ...header,
                    ...patch,
                });
            },
            [
                header,
                onChange,
            ]
        );

    const handleDocumentDateChange =
        useCallback(
            (
                nextDate
            ) => {
                const patch = {
                    documentDate:
                        nextDate,
                };

                if (
                    autoCalculateDueDate
                ) {
                    patch.dueDate =
                        addDays(
                            nextDate,
                            header
                                .paymentTermDays
                        );
                }

                emitChange(
                    patch
                );
            },
            [
                autoCalculateDueDate,
                emitChange,
                header.paymentTermDays,
            ]
        );

    const handlePaymentTermChange =
        useCallback(
            (
                nextDays
            ) => {
                const days =
                    Number(
                        nextDays ||
                        0
                    );

                emitChange({
                    paymentTermDays:
                        days,

                    dueDate:
                        autoCalculateDueDate
                            ? addDays(
                                header
                                    .documentDate,
                                days
                            )
                            : header
                                .dueDate,
                });
            },
            [
                autoCalculateDueDate,
                emitChange,
                header.documentDate,
                header.dueDate,
            ]
        );

    const title =
        documentType ===
        "QUOTATION"
            ? "Quotation Details"
            : documentType ===
                "PROFORMA_INVOICE"
                ? "Proforma Details"
                : "Invoice Details";

    const numberLabel =
        documentType ===
        "QUOTATION"
            ? "Quotation Number"
            : documentType ===
                "PROFORMA_INVOICE"
                ? "Proforma Number"
                : "Invoice Number";

    return (
        <section className={[
            "invoice-header-section",
            locationOpen ? "invoice-header-location-open" : "",
            supplyOpen ? "invoice-header-supply-open" : "",
            deliveryOpen ? "invoice-header-delivery-open" : "",
        ].filter(Boolean).join(" ")}>
            <div className="invoice-header-section-heading">
                <div className="invoice-header-title">
                    <span className="invoice-header-title-icon">
                        <FiFileText />
                    </span>

                    <div>
                        <h2>
                            {title}
                        </h2>

                        <p>
                            Configure dates, numbering, supply details, sales channel and fulfilment information.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    className="invoice-header-refresh-button"
                    disabled={
                        disabled
                    }
                    onClick={() =>
                        emitChange({
                            documentDate:
                                toDateInputValue(
                                    new Date()
                                ),

                            dueDate:
                                addDays(
                                    toDateInputValue(
                                        new Date()
                                    ),
                                    header
                                        .paymentTermDays
                                ),
                        })
                    }
                >
                    <FiRefreshCw />

                    Use Today
                </button>
            </div>

            <div className="invoice-header-toolbar">
                <button type="button" className={locationOpen ? "invoice-header-toolbar-button active" : "invoice-header-toolbar-button"} onClick={() => setLocationOpen((v) => !v)}>
                    <FiShoppingBag /> Business Location
                </button>
                <button type="button" className={supplyOpen ? "invoice-header-toolbar-button active" : "invoice-header-toolbar-button"} onClick={() => setSupplyOpen((v) => !v)}>
                    <FiMapPin /> Place of Supply
                </button>
                <button type="button" className={deliveryOpen ? "invoice-header-toolbar-button active" : "invoice-header-toolbar-button"} onClick={() => setDeliveryOpen((v) => !v)}>
                    <FiTruck /> Delivery & Transport
                </button>
            </div>

            <div className="invoice-header-grid invoice-header-grid-four">
                <div className="invoice-header-field">
                    <label>
                        {numberLabel}
                    </label>

                    <div className="invoice-header-input-with-icon">
                        <FiHash />

                        <input
                            type="text"
                            value={
                                header
                                    .documentNumber
                            }
                            disabled
                            placeholder="Auto generated"
                        />
                    </div>
                </div>

                <div className="invoice-header-field">
                    <label>
                        Document Date
                        {requireDocumentDate && (
                            <span>
                                *
                            </span>
                        )}
                    </label>

                    <div className="invoice-header-input-with-icon">
                        <FiCalendar />

                        <input
                            type="date"
                            value={
                                header
                                    .documentDate
                            }
                            disabled={
                                disabled
                            }
                            onChange={(
                                event
                            ) =>
                                handleDocumentDateChange(
                                    event.target
                                        .value
                                )
                            }
                        />
                    </div>

                    {errors.documentDate && (
                        <small className="invoice-header-field-error">
                            {errors.documentDate}
                        </small>
                    )}
                </div>

                {showDueDate && (
                    <div className="invoice-header-field">
                        <label>
                            Due Date
                            {requireDueDate && (
                                <span>
                                    *
                                </span>
                            )}
                        </label>

                        <div className="invoice-header-input-with-icon">
                            <FiCalendar />

                            <input
                                type="date"
                                value={
                                    header
                                        .dueDate
                                }
                                disabled={
                                    disabled
                                }
                                onChange={(
                                    event
                                ) =>
                                    emitChange({
                                        dueDate:
                                            event.target
                                                .value,
                                    })
                                }
                            />
                        </div>

                        {errors.dueDate && (
                            <small className="invoice-header-field-error">
                                {errors.dueDate}
                            </small>
                        )}
                    </div>
                )}

                {showDueDate && (
                    <div className="invoice-header-field">
                        <label>
                            Payment Terms
                        </label>

                        <div className="invoice-header-select-with-icon">
                            <FiCalendar />

                            <select
                                value={
                                    header
                                        .paymentTermDays
                                }
                                disabled={
                                    disabled
                                }
                                onChange={(
                                    event
                                ) =>
                                    handlePaymentTermChange(
                                        event.target
                                            .value
                                    )
                                }
                            >
                                {PAYMENT_TERM_OPTIONS.map(
                                    (
                                        option
                                    ) => (
                                        <option
                                            key={
                                                option.value
                                            }
                                            value={
                                                option.value
                                            }
                                        >
                                            {option.label}
                                        </option>
                                    )
                                )}
                            </select>

                            <FiChevronDown className="invoice-header-select-arrow" />
                        </div>
                    </div>
                )}

                {showReference && (
                    <div className="invoice-header-field">
                        <label>
                            Reference Number
                        </label>

                        <input
                            type="text"
                            value={
                                header
                                    .referenceNumber
                            }
                            disabled={
                                disabled
                            }
                            placeholder="Optional reference"
                            onChange={(
                                event
                            ) =>
                                emitChange({
                                    referenceNumber:
                                        event.target
                                            .value,
                                })
                            }
                        />
                    </div>
                )}

                {showPurchaseOrder && (
                    <div className="invoice-header-field">
                        <label>
                            Purchase Order Number
                        </label>

                        <input
                            type="text"
                            value={
                                header
                                    .purchaseOrderNumber
                            }
                            disabled={
                                disabled
                            }
                            placeholder="Customer PO number"
                            onChange={(
                                event
                            ) =>
                                emitChange({
                                    purchaseOrderNumber:
                                        event.target
                                            .value,
                                })
                            }
                        />
                    </div>
                )}

                {showSalesperson && (
                    <div className="invoice-header-field">
                        <label>
                            Salesperson
                        </label>

                        <div className="invoice-header-select-with-icon">
                            <FiUserCheck />

                            <select
                                value={
                                    header
                                        .salespersonId
                                }
                                disabled={
                                    disabled
                                }
                                onChange={(
                                    event
                                ) => {
                                    const selected =
                                        salespersons.find(
                                            (
                                                salesperson
                                            ) =>
                                                String(
                                                    salesperson
                                                        ?._id ||
                                                    salesperson
                                                        ?.id ||
                                                    ""
                                                ) ===
                                                String(
                                                    event
                                                        .target
                                                        .value
                                                )
                                        );

                                    emitChange({
                                        salespersonId:
                                            event.target
                                                .value,

                                        salesperson:
                                            selected
                                                ?.name ||
                                            selected
                                                ?.fullName ||
                                            selected
                                                ?.customerName ||
                                            "",
                                    });
                                }}
                            >
                                <option value="">
                                    Select salesperson
                                </option>

                                {salespersons.map(
                                    (
                                        salesperson
                                    ) => {
                                        const id =
                                            salesperson
                                                ?._id ||
                                            salesperson
                                                ?.id ||
                                            "";

                                        const name =
                                            salesperson
                                                ?.name ||
                                            salesperson
                                                ?.fullName ||
                                            salesperson
                                                ?.customerName ||
                                            salesperson
                                                ?.email ||
                                            "Unnamed";

                                        return (
                                            <option
                                                key={
                                                    id
                                                }
                                                value={
                                                    id
                                                }
                                            >
                                                {name}
                                            </option>
                                        );
                                    }
                                )}
                            </select>

                            <FiChevronDown className="invoice-header-select-arrow" />
                        </div>
                    </div>
                )}

                {showSalesChannel && (
                    <div className="invoice-header-field">
                        <label>
                            Sales Channel
                        </label>

                        <div className="invoice-header-select-with-icon">
                            <FiShoppingBag />

                            <select
                                value={
                                    header
                                        .salesChannel
                                }
                                disabled={
                                    disabled
                                }
                                onChange={(
                                    event
                                ) =>
                                    emitChange({
                                        salesChannel:
                                            event.target
                                                .value,
                                    })
                                }
                            >
                                {SALES_CHANNELS.map(
                                    (
                                        option
                                    ) => (
                                        <option
                                            key={
                                                option.value
                                            }
                                            value={
                                                option.value
                                            }
                                        >
                                            {option.label}
                                        </option>
                                    )
                                )}
                            </select>

                            <FiChevronDown className="invoice-header-select-arrow" />
                        </div>
                    </div>
                )}

                {showSourceType && (
                    <div className="invoice-header-field">
                        <label>
                            Source
                        </label>

                        <select
                            value={
                                header
                                    .sourceType
                            }
                            disabled={
                                disabled
                            }
                            onChange={(
                                event
                            ) =>
                                emitChange({
                                    sourceType:
                                        event.target
                                            .value,
                                })
                            }
                        >
                            {SOURCE_TYPES.map(
                                (
                                    option
                                ) => (
                                    <option
                                        key={
                                            option.value
                                        }
                                        value={
                                            option.value
                                        }
                                    >
                                        {option.label}
                                    </option>
                                )
                            )}
                        </select>
                    </div>
                )}
            </div>

            {(showBranch ||
                showFirm ||
                showWarehouse) && (
                <div className="invoice-header-subsection invoice-header-location-section">
                    <div className="invoice-header-subsection-title">
                        <FiShoppingBag />

                        <div>
                            <strong>
                                Business Location
                            </strong>

                            <span>
                                Select the firm, branch and warehouse used for billing and stock posting.
                            </span>
                        </div>
                    </div>

                    <div className="invoice-header-grid invoice-header-grid-three">
                        {showFirm && (
                            <div className="invoice-header-field">
                                <label>
                                    Firm
                                </label>

                                <select
                                    value={
                                        header
                                            .firmId
                                    }
                                    disabled={
                                        disabled
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        emitChange({
                                            firmId:
                                                event.target
                                                    .value,
                                        })
                                    }
                                >
                                    <option value="">
                                        Default firm
                                    </option>

                                    {firms.map(
                                        (
                                            firm
                                        ) => {
                                            const id =
                                                firm
                                                    ?._id ||
                                                firm
                                                    ?.id ||
                                                "";

                                            const name =
                                                firm
                                                    ?.name ||
                                                firm
                                                    ?.firmName ||
                                                firm
                                                    ?.businessName ||
                                                "Unnamed firm";

                                            return (
                                                <option
                                                    key={
                                                        id
                                                    }
                                                    value={
                                                        id
                                                    }
                                                >
                                                    {name}
                                                </option>
                                            );
                                        }
                                    )}
                                </select>
                            </div>
                        )}

                        {showBranch && (
                            <div className="invoice-header-field">
                                <label>
                                    Branch
                                </label>

                                <select
                                    value={
                                        header
                                            .branchId
                                    }
                                    disabled={
                                        disabled
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        emitChange({
                                            branchId:
                                                event.target
                                                    .value,
                                        })
                                    }
                                >
                                    <option value="">
                                        Default branch
                                    </option>

                                    {branches.map(
                                        (
                                            branch
                                        ) => {
                                            const id =
                                                branch
                                                    ?._id ||
                                                branch
                                                    ?.id ||
                                                "";

                                            const name =
                                                branch
                                                    ?.name ||
                                                branch
                                                    ?.branchName ||
                                                "Unnamed branch";

                                            return (
                                                <option
                                                    key={
                                                        id
                                                    }
                                                    value={
                                                        id
                                                    }
                                                >
                                                    {name}
                                                </option>
                                            );
                                        }
                                    )}
                                </select>
                            </div>
                        )}

                        {showWarehouse && (
                            <div className="invoice-header-field">
                                <label>
                                    Warehouse
                                </label>

                                <select
                                    value={
                                        header
                                            .warehouseId
                                    }
                                    disabled={
                                        disabled
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        emitChange({
                                            warehouseId:
                                                event.target
                                                    .value,
                                        })
                                    }
                                >
                                    <option value="">
                                        Default warehouse
                                    </option>

                                    {warehouses.map(
                                        (
                                            warehouse
                                        ) => {
                                            const id =
                                                warehouse
                                                    ?._id ||
                                                warehouse
                                                    ?.id ||
                                                "";

                                            const name =
                                                warehouse
                                                    ?.name ||
                                                warehouse
                                                    ?.warehouseName ||
                                                "Unnamed warehouse";

                                            return (
                                                <option
                                                    key={
                                                        id
                                                    }
                                                    value={
                                                        id
                                                    }
                                                >
                                                    {name}
                                                </option>
                                            );
                                        }
                                    )}
                                </select>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showPlaceOfSupply && (
                <div className="invoice-header-subsection invoice-header-supply-section">
                    <div className="invoice-header-subsection-title">
                        <FiMapPin />

                        <div>
                            <strong>
                                Place of Supply
                            </strong>

                            <span>
                                Determines whether CGST/SGST or IGST is applied.
                            </span>
                        </div>
                    </div>

                    <div className="invoice-header-grid invoice-header-grid-three">
                        <div className="invoice-header-field">
                            <label>
                                State
                            </label>

                            <input
                                type="text"
                                value={
                                    header
                                        .placeOfSupply
                                }
                                disabled={
                                    disabled
                                }
                                placeholder="Rajasthan"
                                onChange={(
                                    event
                                ) =>
                                    emitChange({
                                        placeOfSupply:
                                            event.target
                                                .value,
                                    })
                                }
                            />
                        </div>

                        <div className="invoice-header-field">
                            <label>
                                State Code
                            </label>

                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={
                                    2
                                }
                                value={
                                    header
                                        .placeOfSupplyStateCode
                                }
                                disabled={
                                    disabled
                                }
                                placeholder="08"
                                onChange={(
                                    event
                                ) =>
                                    emitChange({
                                        placeOfSupplyStateCode:
                                            String(
                                                event.target
                                                    .value
                                            )
                                                .replace(
                                                    /\D/g,
                                                    ""
                                                )
                                                .slice(
                                                    0,
                                                    2
                                                ),
                                    })
                                }
                            />

                            {errors
                                .placeOfSupplyStateCode && (
                                <small className="invoice-header-field-error">
                                    {errors
                                        .placeOfSupplyStateCode}
                                </small>
                            )}
                        </div>

                        <label className="invoice-header-tax-toggle">
                            <input
                                type="checkbox"
                                checked={
                                    header
                                        .isInterState
                                }
                                disabled={
                                    disabled
                                }
                                onChange={(
                                    event
                                ) =>
                                    emitChange({
                                        isInterState:
                                            event.target
                                                .checked,
                                    })
                                }
                            />

                            <span>
                                Interstate supply (apply IGST)
                            </span>
                        </label>
                    </div>
                </div>
            )}

            {showShipping && (
                <div className="invoice-header-subsection invoice-header-delivery-section">
                    <div className="invoice-header-subsection-title">
                        <FiTruck />

                        <div>
                            <strong>
                                Delivery & Transport
                            </strong>

                            <span>
                                Optional dispatch details printed on the invoice.
                            </span>
                        </div>
                    </div>

                    <div className="invoice-header-grid invoice-header-grid-three">
                        <div className="invoice-header-field">
                            <label>
                                Shipping Method
                            </label>

                            <input
                                type="text"
                                value={
                                    header
                                        .shippingMethod
                                }
                                disabled={
                                    disabled
                                }
                                placeholder="Self pickup, courier, transport"
                                onChange={(
                                    event
                                ) =>
                                    emitChange({
                                        shippingMethod:
                                            event.target
                                                .value,
                                    })
                                }
                            />
                        </div>

                        <div className="invoice-header-field">
                            <label>
                                Transporter Name
                            </label>

                            <input
                                type="text"
                                value={
                                    header
                                        .transporterName
                                }
                                disabled={
                                    disabled
                                }
                                placeholder="Transport company"
                                onChange={(
                                    event
                                ) =>
                                    emitChange({
                                        transporterName:
                                            event.target
                                                .value,
                                    })
                                }
                            />
                        </div>

                        <div className="invoice-header-field">
                            <label>
                                Vehicle Number
                            </label>

                            <input
                                type="text"
                                value={
                                    header
                                        .vehicleNumber
                                }
                                disabled={
                                    disabled
                                }
                                placeholder="RJ14AB1234"
                                onChange={(
                                    event
                                ) =>
                                    emitChange({
                                        vehicleNumber:
                                            event.target
                                                .value
                                                .toUpperCase(),
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export {
    EMPTY_INVOICE_HEADER,
    SALES_CHANNELS,
    SOURCE_TYPES,
    PAYMENT_TERM_OPTIONS,
    normalizeHeaderValue,
    validateInvoiceHeader,
    addDays,
    toDateInputValue,
};

export default React.memo(InvoiceHeader);